/**
 * /admin/uni-verification — leaderboard of claimed universities
 * ranked by how many distinct registry fields they've verified.
 *
 * Ops uses this to see which claims are engaged and which need
 * chasing. Rows link to the university's edit page + public page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trophy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function Medal({ rank }) {
  if (rank === 0) return <span title="1st">🥇</span>;
  if (rank === 1) return <span title="2nd">🥈</span>;
  if (rank === 2) return <span title="3rd">🥉</span>;
  return <span className="text-stone-400 font-mono">#{rank + 1}</span>;
}

export default function AdminUniVerification() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/uni-verification-leaderboard");
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (authLoading) {
    return <div className="p-6 text-stone-400 text-sm"><Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Loading…</div>;
  }
  if (!user) {
    return <div className="p-6 text-stone-600">Sign in as admin to view this page.</div>;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-900"
          data-testid="uni-verification-back"
        >
          <ArrowLeft className="h-3 w-3" /> Admin home
        </Link>
        <div className="mt-4 flex items-baseline gap-3">
          <Trophy className="h-5 w-5 text-amber-600" />
          <h1 className="font-editorial text-4xl sm:text-5xl text-stone-900">
            Uni verification leaderboard
          </h1>
        </div>
        <p className="mt-2 text-stone-600 text-sm max-w-2xl">
          Claimed universities ranked by how many distinct registry
          fields they've verified. Nudge the ones at the bottom.
        </p>

        <div
          className="mt-6 border border-stone-200 rounded-xl bg-white overflow-hidden"
          data-testid="uni-verification-table"
        >
          {err ? (
            <div className="p-6 text-sm text-rose-700">Error: {err}</div>
          ) : items === null ? (
            <div className="p-6 text-sm text-stone-500">
              <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-stone-500" data-testid="uni-verification-empty">
              No claimed universities yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[10px] font-underground-sc tracking-widest text-stone-500">
                  <th className="text-left px-3 py-2 w-10">RANK</th>
                  <th className="text-left px-3 py-2">UNIVERSITY</th>
                  <th className="text-right px-3 py-2">UNIQUE FIELDS</th>
                  <th className="text-right px-3 py-2">TOTAL EDITS</th>
                  <th className="text-right px-3 py-2">DEGREES TOUCHED</th>
                  <th className="text-right px-3 py-2">LAST EDIT</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const cold = row.unique_fields_touched === 0;
                  return (
                    <tr
                      key={row.university_id}
                      className={`border-b border-stone-100 last:border-0 ${
                        cold ? "bg-amber-50/40" : ""
                      }`}
                      data-testid={`uni-verification-row-${row.university_id}`}
                    >
                      <td className="px-3 py-2.5 text-center"><Medal rank={idx} /></td>
                      <td className="px-3 py-2.5">
                        <Link
                          to={`/uni/${row.university_id}`}
                          className="text-stone-900 hover:text-purple-800 hover:underline"
                        >
                          {row.name || row.university_id}
                        </Link>
                        <div className="text-[10px] text-stone-400 font-mono">
                          {row.university_id} · {row.owners_count} owner{row.owners_count === 1 ? "" : "s"}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded ${
                            cold
                              ? "bg-amber-100 text-amber-900"
                              : row.unique_fields_touched >= 5
                              ? "bg-emerald-50 text-emerald-800"
                              : "text-stone-800"
                          }`}
                        >
                          {row.unique_fields_touched}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-stone-600">
                        {row.verified_field_count}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-stone-600">
                        {row.degrees_touched}
                      </td>
                      <td className="px-3 py-2.5 text-right text-[11px] text-stone-500">
                        <div>{fmt(row.last_edit_at)}</div>
                        {row.last_edit_by && (
                          <div className="text-[10px] text-stone-400 font-mono truncate max-w-[160px]">
                            {row.last_edit_by}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Link
                          to={`/uni-portal/uni/${row.university_id}`}
                          className="text-[11px] font-underground-sc tracking-wider text-purple-700 hover:text-purple-900"
                        >
                          Open portal →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="mt-4 text-[11px] text-stone-500 max-w-2xl">
          <strong className="text-amber-800">Amber row</strong> = 0 unique fields verified — needs chasing.{" "}
          <strong className="text-emerald-800">Emerald column</strong> = ≥ 5 fields, healthy engagement.{" "}
          Ranking prefers unique-fields-touched over raw-edit-count so
          the leaderboard rewards breadth, not repetition.
        </p>
      </div>
    </div>
  );
}
